import Button, { ButtonProps } from "@ui-kit/form/Button/Button.web";
import useRedirect from "@shared/hooks/useRedirect";
import { enums } from "@shared/constants";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { UserBasedTicketBasketHoldsSerializer } from "@app-contexts/user/features/basket/data/types/basket";
import {
  eventHasWaitlistItems,
  getAllowedRegions,
  isEventAvailableForQuantityRequested,
  isPastEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import { useRouter } from "next/router";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import React, { useMemo } from "react";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";

interface DetailPageButtonProps {
  saleStartsOnPassed?: boolean;
  eventData?: PublicBaseEventSerializer;
  membershipGroupData?: PublicMembershipGroupSerializer;
  holdDetailsData?: UserBasedTicketBasketHoldsSerializer;
  openModal: () => void;
  props?: ButtonProps;
}

/**
 * Displays a button that opens the ticket list modal or membership list modal.
 */
export const DetailPageButton: React.FC<DetailPageButtonProps> = ({
  saleStartsOnPassed = true,
  eventData = null,
  membershipGroupData = null,
  holdDetailsData = null,
  openModal,
  props = {
    colorPalette: "secondary",
    minHeight: "40px",
    minWidth: "147px",
  },
}) => {
  const { redirect } = useRedirect();
  const router = useRouter();
  const { t } = useTranslation("purchases");

  const eventItemType = enums.DISCOVERY_VIEW_TYPE_MAP.event.id;

  const shouldRefresh = useMemo(
    () => !saleStartsOnPassed,
    [saleStartsOnPassed]
  );

  // check for at least 1 waitlisted item or event.is_waitlisted
  const hasWaitlist = useMemo(
    () => eventHasWaitlistItems(eventData),
    [eventData]
  );

  // check for hold data as part of the sold out condition
  const soldOutEvent = useMemo(
    () => isSoldOutEvent(eventData) && !holdDetailsData,
    [eventData, holdDetailsData]
  );

  // "not available" is a frontend only concept and is not a backend field.
  const eventNotAvailable = useMemo(
    () =>
      eventData ? !isEventAvailableForQuantityRequested(eventData) : false,
    [eventData]
  );

  // TODO: update this function if sold_out field is added to membership groups
  const isDisabledButton = (): boolean => {
    let disabled = false;

    if (eventData) {
      // disable button for events that are not in allowed regions
      const allowedRegions = getAllowedRegions(eventData.allowed_regions);
      const displayAllowedRegionsText =
        allowedRegions && !eventData.region_is_allowed;
      // disable button for events that don't require tickets
      const ticketsNotRequired = eventData.no_ticket_types;
      // disable button for past events
      const pastEvent = isPastEvent(eventData);

      disabled =
        displayAllowedRegionsText ||
        (soldOutEvent && !hasWaitlist) ||
        eventNotAvailable ||
        ticketsNotRequired ||
        pastEvent;
    }

    return disabled;
  };

  const getButtonText = (): string => {
    let text = "";

    if (eventData) {
      if (soldOutEvent) {
        // when event is sold out AND has a waitlist, show Waitlist text
        if (hasWaitlist) {
          text = t("Join Waitlist", { ns: "common" });
        } else {
          // otherwise, show Sold Out text
          text = t("Sold Out");
        }
      }
      // for other cases where the button is disabled, show Not Available text
      else if (
        eventNotAvailable ||
        // TODO improve intention here as soldOutEvent is part of the isDisabledButton conditions
        (isDisabledButton() && !soldOutEvent)
      ) {
        text = t("Not available");
      }
      // when button is enabled, show initiate purchase button text
      else {
        const PURCHASE_BUTTON_ENUM =
          enums.INITIATE_PURCHASE_BUTTON_DESCRIPTION_MAP_BY_ID;
        text =
          t(PURCHASE_BUTTON_ENUM[eventData?.initiate_purchase_button]?.name, {
            ns: "constants",
          }) || t("Find Tickets");
      }
    } else if (membershipGroupData) {
      text = `${t("Buy memberships", {
        type: t(
          enums.MEMBERSHIP_GROUP_VERBIAGE_MAP_BY_ID[
            membershipGroupData.verbiage
          ]?.name,
          { ns: "constants" }
        ),
      })}`;
    }

    return text?.toUpperCase();
  };

  const handleClick = (): void => {
    if (shouldRefresh) {
      window.location.reload();
      return;
    }
    // Check if redirect_tickets_url is present and redirect to that URL
    if (
      eventData?.external_link &&
      eventData.third_party_redirect_option ===
        enums.THIRD_PARTY_REDIRECT_OPTIONS_BY_ID.buy_now.id
    ) {
      redirect(eventData.external_link, {
        asWindow: true,
      });
      return;
    }
    // redirect to the assigned seating page if the event has a space
    // and isn't recurring (modal must open to select child seating events)
    if (
      eventData &&
      eventData.assigned_space &&
      !eventData.is_recurring_parent
    ) {
      let seatingUrl = PUBLIC_ROUTES.EVENTS.seating(eventData.slug);
      // if the event detail page has a url param for source_channel,
      // persist that param on the seating page
      if (router?.query?.source_channel) {
        seatingUrl = seatingUrl.concat(
          `?source_channel=${router?.query?.source_channel}`
        );
      }

      redirect(seatingUrl);
    }
    // redirect to the assigned seating page if reserved_seating_benefits exists
    // and at least 1 benefit has a valid space id
    else if (
      membershipGroupData &&
      membershipGroupData.reserved_seating_benefits &&
      membershipGroupData.reserved_seating_benefits.some(
        (benefit) => benefit.space > 0
      )
    ) {
      redirect(PUBLIC_ROUTES.MEMBERSHIPS.seating(membershipGroupData.slug));
    } else {
      openModal();
    }
  };

  return (
    <Track.Click
      analytics={{
        label: "detail-page-buy-button",
        event_id: eventData?.id,
        venue_id: eventData?.venue?.id,
        custom_params: {
          is_sponsored: eventData?.is_sponsored,
          item_type: eventItemType,
          item_id: eventData?.id,
        },
      }}
    >
      <Button
        onClick={handleClick}
        disabled={isDisabledButton()}
        variant="solid"
        {...props}
        data-testid="buy-button"
        hidden={Boolean(eventData?.no_ticket_types)}
      >
        <Text fontWeight="bold">{getButtonText()}</Text>
      </Button>
    </Track.Click>
  );
};

export default DetailPageButton;
